// Class bell schedule. You set your school's bell times in Settings → School bell;
// this fires a small in-app reminder a few minutes before each one (and, optionally,
// a soft chime + a desktop notification) so the bell never catches you off guard. It
// also paints a "next bell" line on the start screen. Everything is client-side and
// self-contained (no files, no network) - the chime is generated with Web Audio.
import { announce as say } from "./announce.js";
import { get, subscribe } from "../shared/settings-store.js";
import { emitIsland } from "./island-gate.js";
import { unlockOnGesture } from "../shared/audio-unlock.js";

let timer = null;
let fired = new Set(); // keys of reminders already shown today
let firedDay = -1; // calendar day the `fired` set belongs to
let toastEl = null;
let toastHideT = null;
let ac = null; // lazily-created AudioContext (shared with the chime)

// --- helpers ---------------------------------------------------------------
function pad(n) { return n < 10 ? "0" + n : "" + n; }
function fmtTime(d) {
	let h = d.getHours();
	const m = d.getMinutes();
	if (get("clock24h")) return pad(h) + ":" + pad(m);
	const ap = h < 12 ? "AM" : "PM";
	h = h % 12; if (h === 0) h = 12;
	return h + ":" + pad(m) + " " + ap;
}
// Parse a stored "HH:MM" against `now`, returning a Date today or null if malformed.
function bellDate(now, t) {
	const m = /^(\d{1,2}):(\d{2})$/.exec(String(t || "").trim());
	if (!m) return null;
	const h = +m[1], mi = +m[2];
	if (h > 23 || mi > 59) return null;
	const d = new Date(now);
	d.setHours(h, mi, 0, 0);
	return d;
}
function activeDays() {
	const d = get("bellDays");
	return Array.isArray(d) ? d : [1, 2, 3, 4, 5];
}
function schedule() {
	const s = get("bellSchedule");
	return Array.isArray(s) ? s : [];
}

// --- chime (Web Audio; a soft three-note rise) -----------------------------
function chime() {
	try {
		if (!ac) ac = new (window.AudioContext || window.webkitAudioContext)();
		if (ac.state === "suspended") ac.resume().catch(() => {});
		const now = ac.currentTime;
		const notes = [660, 880, 990]; // E5, A5, B5 - a gentle, non-alarming lift
		const master = ac.createGain();
		master.gain.value = 0.18;
		master.connect(ac.destination);
		notes.forEach((f, i) => {
			const o = ac.createOscillator();
			const g = ac.createGain();
			o.type = "sine";
			o.frequency.value = f;
			const start = now + i * 0.16;
			g.gain.setValueAtTime(0, start);
			g.gain.linearRampToValueAtTime(1, start + 0.02);
			g.gain.exponentialRampToValueAtTime(0.001, start + 0.5);
			o.connect(g); g.connect(master);
			o.start(start); o.stop(start + 0.55);
		});
	} catch (e) {}
}

// --- toast (reuses the .rv-update surface styling, glass-theme aware) -------
function showBellToast(text) {
	if (!toastEl) {
		toastEl = document.createElement("div");
		toastEl.className = "rv-update rv-bell";
		toastEl.setAttribute("role", "group");
		toastEl.setAttribute("aria-label", "School bell");
		const ic = document.createElement("span");
		ic.className = "rv-bell-ic";
		ic.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"></path><path d="M13.7 21a2 2 0 0 1-3.4 0"></path></svg>';
		const msg = document.createElement("span");
		msg.className = "rv-update-msg rv-bell-msg";
		const x = document.createElement("button");
		x.type = "button";
		x.className = "rv-update-x";
		x.setAttribute("aria-label", "Dismiss");
		x.textContent = "✕";
		x.addEventListener("click", hideBellToast);
		toastEl.append(ic, msg, x);
		document.body.appendChild(toastEl);
	}
	toastEl.querySelector(".rv-bell-msg").textContent = text;
	say(text);
	requestAnimationFrame(() => toastEl && toastEl.classList.add("rv-update-in"));
	clearTimeout(toastHideT);
	toastHideT = setTimeout(hideBellToast, 15000);
}
function hideBellToast() {
	clearTimeout(toastHideT);
	if (!toastEl) return;
	toastEl.classList.remove("rv-update-in");
	const el = toastEl; toastEl = null;
	setTimeout(() => el.remove(), 260);
}

function notify(text) {
	if (!get("bellNotify")) return;
	try { if (window.Notification && Notification.permission === "granted") new Notification("Re:Vault", { body: text, tag: "revault-bell" }); } catch (e) {}
}

function announce(text) {
	showBellToast(text);
	emitIsland("bell", { text, at: Date.now() });
	if (get("bellChime")) chime();
	notify(text);
}

// --- start-screen "next bell" line -----------------------------------------
let nextBell = null;
// The next bell today, for the island's Study section: { at, label } or null.
export const bellNext = () => nextBell;
function paintNext(nextDate, nextLabel) {
	nextBell = get("bellEnabled") && nextDate ? { at: nextDate.getTime(), label: nextLabel || "", text: fmtTime(nextDate) } : null;
	const el = document.getElementById("rv-nextbell");
	if (!el) return;
	if (!get("bellEnabled") || !nextDate) { el.hidden = true; el.textContent = ""; return; }
	const mins = Math.max(0, Math.round((nextDate.getTime() - Date.now()) / 60000));
	const when = mins <= 0 ? "now" : mins < 60 ? "in " + mins + " min" : "in " + Math.floor(mins / 60) + "h " + (mins % 60) + "m";
	el.hidden = false;
	el.textContent = "Next bell " + fmtTime(nextDate) + (nextLabel ? " · " + nextLabel : "") + " · " + when;
}

// --- the tick --------------------------------------------------------------
function tick() {
	const now = new Date();
	if (now.getDate() !== firedDay) { fired = new Set(); firedDay = now.getDate(); }

	if (!get("bellEnabled") || activeDays().indexOf(now.getDay()) === -1) { paintNext(null); return; }
	const warn = Math.max(0, Math.min(30, Number(get("bellWarnMin")) || 0));
	const dayKey = now.getFullYear() + "-" + now.getMonth() + "-" + now.getDate();

	let next = null, nextLabel = "";
	for (const item of schedule()) {
		const d = bellDate(now, item && item.t);
		if (!d) continue;
		const label = (item.label || "").toString().slice(0, 40);
		const untilMs = d.getTime() - now.getTime();
		const untilMin = untilMs / 60000;

		// Warning window: fire once when we're within `warn` minutes (but not past the bell).
		if (warn > 0 && untilMin > 0 && untilMin <= warn) {
			const k = dayKey + "|" + item.t + "|warn";
			if (!fired.has(k)) { fired.add(k); const m = Math.max(1, Math.ceil(untilMin)); announce("Bell in " + m + " min" + (label ? " · " + label : "")); }
		}
		// At the bell: fire once within the first minute after it rings.
		if (untilMs <= 0 && untilMs > -60000) {
			const k = dayKey + "|" + item.t + "|now";
			if (!fired.has(k)) { fired.add(k); announce((label ? label + " · " : "") + "Bell now"); }
		}
		// Track the soonest upcoming bell for the start-screen line.
		if (untilMs > -60000 && (!next || d.getTime() < next.getTime())) { next = d; nextLabel = label; }
	}
	paintNext(next, nextLabel);
}

// Self-rescheduling loop aligned a little after each 5s boundary; light enough to
// run always, precise enough for minute-based reminders. Stays on even when the
// feature is off (the tick early-returns) so toggling it needs no restart.
function loop() {
	try { tick(); } catch (e) {}
	clearTimeout(timer);
	const ms = 5000 - (Date.now() % 5000) + 20;
	timer = setTimeout(loop, ms);
}

export function initBell() {
	// A bell rings minutes after the last tap, and Safari only starts Web Audio inside one.
	unlockOnGesture(window, () => get("bellEnabled") === true && get("bellChime") !== false && schedule().length > 0, () => (ac = ac || new (window.AudioContext || window.webkitAudioContext)()));
	loop();
	// Re-run immediately when the schedule/toggle changes so the start-screen line and
	// any in-window reminder update without waiting for the next 5s tick.
	subscribe(() => { try { tick(); } catch (e) {} });
}
